import React, { useEffect, useRef, useState } from 'react'
import * as Icons from 'react-feather'
import DataTable from 'react-data-table-component'
import { Link } from 'react-router-dom'
import axios from 'axios'
import SKAlerts, { CreateAlert } from '../../shared/common-alerts/SKAlerts'
import { ReactComponent as SortArrow } from '../../shared/img/chevron-up-down.svg'
import { getApiBaseUrl } from '../../utils/apiConfig'
import { getStorageValues } from '../../shared/CommonFunctions'
import StatusGridDropdown from '../../shared/common-dropdown/StatusGridDropdown'
import RTOFollowup from './RTOFollowup'
import RTOCompleteModal from './RTOCompleteModal'

function RTOGrid({ rtoList = [], onView, onEmail, fetchRTORegisters, UserPermissions = {}, moduleName = 'RTO', DefaultExpandedRowId = null }) {
  const [filterText, setFilterText] = useState('')
  const [alert, setAlert] = useState(null)
  const [editableCell, setEditableCell] = useState({ rowId: null, column: null })
  const [expandedRowId, setExpandedRowId] = useState(null)
  const [statusModalOpen, setStatusModalOpen] = useState(false)
  const [statusModalData, setStatusModalData] = useState({ row: null, option: null })
  const [vehicleRegistrationNumber, setVehicleRegistrationNumber] = useState('')
  const [statusSaving, setStatusSaving] = useState(false)
  const followupRef = useRef(null)

  useEffect(() => {
    if (DefaultExpandedRowId) {
      setExpandedRowId(DefaultExpandedRowId)
    }
  }, [DefaultExpandedRowId])

  const rows = (rtoList || []).filter((item) => {
    const term = filterText.toLowerCase()
    return (
      (item.SalesNumber && item.SalesNumber.toLowerCase().includes(term)) ||
      (item.CustomerName && item.CustomerName.toLowerCase().includes(term)) ||
      (item.MobileNumber && item.MobileNumber.toLowerCase().includes(term)) ||
      (item.PaymentMode && item.PaymentMode.toLowerCase().includes(term))
    )
  })

  const updateStatus = async (option, row) => {
    if (!option || !row) return
    const statusLabel = (option.label || '').toLowerCase()
    const isComplete = statusLabel === 'complete' || statusLabel === 'completed'
    if (isComplete) {
      setStatusModalData({ row, option })
      setVehicleRegistrationNumber(row?.VehicleRegistrationNumber || '')
      setStatusModalOpen(true)
      return
    }

    try {
      await axios.post(getApiBaseUrl() + '/rtoregister/updatestatus', {
        RTORegisterId: row.RTORegisterId,
        StatusId: option.value,
        VehicleRegistrationNumber: row?.VehicleRegistrationNumber || null,
        UserId: getStorageValues('userId')
      })
      CreateAlert('Status updated successfully', 'Success', setAlert)
      fetchRTORegisters && fetchRTORegisters()
    } catch (error) {
      console.log(error)
      CreateAlert(error?.response?.data?.message || 'Unable to update status', 'Error', setAlert)
    }
  }

  const closeStatusModal = () => {
    setStatusModalOpen(false)
    setStatusModalData({ row: null, option: null })
    setVehicleRegistrationNumber('')
  }

  const submitCompleteStatus = async () => {
    const { row, option } = statusModalData
    if (!row || !option) return
    if (!vehicleRegistrationNumber.trim()) {
      CreateAlert('Vehicle Registration Number is required', 'Warning', setAlert)
      return
    }
    setStatusSaving(true)
    try {
      await axios.post(getApiBaseUrl() + '/rtoregister/updatestatus', {
        RTORegisterId: row.RTORegisterId,
        StatusId: option.value,
        VehicleRegistrationNumber: vehicleRegistrationNumber,
        UserId: getStorageValues('userId')
      })
      CreateAlert('Status updated to Completed', 'Success', setAlert)
      closeStatusModal()
      fetchRTORegisters && fetchRTORegisters()
    } catch (error) {
      console.log(error)
      CreateAlert(error?.response?.data?.message || 'Unable to update status', 'Error', setAlert)
    } finally {
      setStatusSaving(false)
    }
  }

  const columns = [
    {
      name: <div className="titlename"><span>Customer Sales#</span></div>,
      selector: row => row.SalesNumber,
      cell: (row) => (
        <span title={row.SalesNumber}>{row.SalesNumber} ({row.CustomerName || '-'})</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />,
      width: '240px'
    },
    {
      name: <div className="titlename"><span>Payment Mode</span></div>,
      selector: row => row.PaymentMode,
      cell: (row) => (
        <span title={row.PaymentMode}>{row.PaymentMode || '-'}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Customer Mobile</span></div>,
      selector: row => row.MobileNumber,
      cell: (row) => (
        <span title={row.MobileNumber}>{row.MobileNumber || '-'}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Status</span></div>,
      selector: row => row.Status,
      cell: (row) => {
        const isCompleted = (row?.Status || '').toLowerCase() === 'completed' || (row?.Status || '').toLowerCase() === 'complete'
        const isEditing = editableCell.rowId === row.RTORegisterId && editableCell.column === 'Status'
        return isEditing ? (
          <StatusGridDropdown
            key={`status-${row.RTORegisterId}`}
            autoFocus={true}
            showLabel={false}
            selectedValue={row.StatusId}
            onChangeDropdown={(e) => {
              updateStatus(e, row)
              setEditableCell({ rowId: null, column: null })
            }}
            onBlur={() => setEditableCell({ rowId: null, column: null })}
            openMenuOnFocus={true}
            openMenuOnClick={true}
          />
        ) : (
          <div
            className="editable-cell-with-icon"
            onClick={() => {
              if (!isCompleted) {
                setEditableCell({ rowId: row.RTORegisterId, column: 'Status' })
              }
            }}
            style={{ cursor: 'pointer', height: '100%', width: '100%' }}
          >
            <div className="cell-content">{row.Status || '-'}</div>
            {!isCompleted && (
              <span className="edit-icon">
                <Icons.Edit size={12} color="#1261a0" />
              </span>
            )}
          </div>
        )
      },
      sortable: true,
      sortIcon: <SortArrow />
    }
  ]

  if (UserPermissions.ViewModule === 1) {
    columns.push({
      name: <div className="titlename">Actions</div>,
      cell: (row) => (
        <div className="edit-delete-action">
          <Link
            className="me-2 p-2"
            to=""
            onClick={() => onView && onView(row)}
          >
            <Icons.Eye data-feather="eye" className="action-eye" />
          </Link>
          <Link
            className="me-2 p-2"
            to=""
            onClick={() => onEmail && onEmail(row)}
            title="Send Email"
          >
            <Icons.Mail data-feather="mail" size="14" className="feather-mail" />
          </Link>
        </div>
      ),
      center: 'true',
      width: '130px'
    })
  }

  const handleRowExpandToggled = (expanded, row) => {
    setExpandedRowId((prev) => (prev === row.RTORegisterId ? null : row.RTORegisterId))
  }

  const isRowExpanded = (row) => row.RTORegisterId === expandedRowId

  return (
    <>
      <SKAlerts alert={alert} />
      <div className="card table-list-card">
        <div className="card-body">
          <div className="table-top">
            <div className="search-set">
              <div className="search-input">
                <button type="button" className="btn btn-searchset">
                  <Icons.Search className="feather-search" />
                </button>
                <input
                  id="search"
                  type="text"
                  className="form-control form-control-sm custom-search-input"
                  placeholder="Search"
                  aria-label="Search Input"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DataTable
            className="table-responsive product-list"
            columns={columns}
            data={rows}
            pagination
            defaultSortField="SalesNumber"
            sortIcon={<SortArrow />}
            persistTableHead={true}
            expandableRows
            onRowExpandToggled={handleRowExpandToggled}
            expandableRowExpanded={isRowExpanded}
            expandableRowsComponent={RTOFollowup}
            expandableRowsComponentProps={{ ref: followupRef }}
          />
        </div>
      </div>
      <RTOCompleteModal
        show={statusModalOpen}
        vehicleRegistrationNumber={vehicleRegistrationNumber}
        onChangeRegistrationNumber={setVehicleRegistrationNumber}
        onClose={closeStatusModal}
        onSubmit={submitCompleteStatus}
        saving={statusSaving}
      />
    </>
  )
}

export default RTOGrid
